import { useState } from "react";
import axios from "axios";
import Modal from "../../Atoms/Modal/Modal";
import Buttons from "../../Atoms/Buttons/Buttons";
import { API_URL } from "../../../Constants";
import {
  EXPIRY_REASON,
  markSessionRenewed,
  saveSession,
} from "../../../services/session";
import { recordActivity } from "../../../services/activity";
import styles from "./Session.module.scss";

/**
 * Sign in again WITHOUT leaving the page.
 *
 * The old behaviour was `navigate('/')` with no explanation: the operator lost
 * the screen they were on and everything unsaved on it, and was never told why.
 * This keeps the page mounted underneath, so a half-written reply or a
 * part-filled form is still there afterwards.
 *
 * NO AUTHORIZATION HEADER IS SENT — deliberately, and this matters.
 *
 * Contract §4.2: credential endpoints must ignore the bearer header, precisely
 * so "a password change revokes existing sessions, but the dead token stays in
 * the browser" cannot lock a user out with a correct password. Both initial
 * login and this fallback therefore omit Authorization explicitly.
 */
const ReauthDialog = ({ reason }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const title =
    reason === EXPIRY_REASON.UNAUTHORIZED
      ? "انتهت صلاحية الجلسة"
      : "انتهت مدة الجلسة";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_URL}/api/Auth/login`,
        { username, password },
        { headers: { "Content-Type": "application/json" } },
      );

      const body = response?.data;

      // `POST /api/Auth/login` does NOT return the contract's
      // `{ success, data: { token } }` envelope — verified against a live
      // response (network tab, 2026-09-22): it is a FLAT object,
      // `{ authenticated, token, tokenExpiry, companyId, cmsPages, message, … }`.
      // `LogInForm.jsx`'s working login path already depends on this same flat
      // shape (its `data` argument is the *whole axios response*, so
      // `data?.data?.token` there resolves to `responseBody.token`).
      //
      // This dialog checked `body.success !== true || !body?.data?.token`,
      // which is never true for this endpoint — `success` does not exist on
      // the body and there is no nested `data`. So a genuinely successful
      // re-authentication (valid token in the response) still fell into the
      // error branch below and told the operator to try again.
      const token = body?.data?.token ?? body?.token;
      const tokenExpiry = body?.data?.tokenExpiry ?? body?.tokenExpiry;
      const companyId = body?.data?.companyId ?? body?.companyId;

      if (!token || body?.authenticated === false || body?.success === false) {
        setError(
          body?.error || body?.message || "تعذّر تسجيل الدخول. حاول مرة أخرى.",
        );
        setBusy(false);
        return;
      }

      saveSession({
        ...body,
        token,
        tokenExpiry,
        companyId,
      });

      // Start the idle clock from now. `clearSession` removed the old stamp, and
      // signing in is unambiguously activity - seeding it here means the new
      // session begins with a full idle allowance instead of waiting for a stray
      // scroll to establish a baseline.
      recordActivity(true);

      // Re-arms the guard and dismisses this dialog. The page underneath was
      // never unmounted, so the operator carries straight on.
      markSessionRenewed();
    } catch (err) {
      const body = err?.response?.data;
      setError(
        body?.error ||
          body?.message ||
          "تعذّر تسجيل الدخول. تأكد من اسم المستخدم وكلمة المرور.",
      );
      setBusy(false);
    }
  };

  const handleFullLogin = () => {
    window.location.href = "/";
  };

  return (
    <Modal
      onClose={() => {}}
      closeWhite={false}
      innerContainerClass={styles["session-modal"]}
      modalInnerContainerStyle={{ padding: 0, gap: 0 }}
      // No dismiss control: there is nothing usable behind this dialog until
      // the operator signs in, and a close button that leaves them on a dead
      // screen firing 401s would be worse than none.
      closeStyle={{ display: "none" }}
    >
      <div className={styles.dialog} dir="rtl" role="alertdialog">
        <header className={styles["dialog-head"]}>
          <h2 className={styles["dialog-title"]}>{title}</h2>
        </header>

        <form onSubmit={handleSubmit}>
          <div className={styles["dialog-body"]}>
            <p className={styles["dialog-text"]}>
              لأسباب أمنية تم إنهاء جلستك. سجّل الدخول للمتابعة من نفس الصفحة
              دون فقدان ما لم يُحفظ.
            </p>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="reauth-username">
                اسم المستخدم
              </label>
              <input
                id="reauth-username"
                className={styles.input}
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="reauth-password">
                كلمة المرور
              </label>
              <input
                id="reauth-password"
                className={styles.input}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
          </div>

          <footer className={styles["dialog-foot"]}>
            <Buttons
              text={busy ? "جارٍ تسجيل الدخول…" : "تسجيل الدخول والمتابعة"}
              rounded={true}
              color="rgba(13, 160, 110, 1)"
              type="submit"
            />
            <button
              type="button"
              className={styles["ghost-button"]}
              onClick={handleFullLogin}
            >
              الذهاب إلى صفحة الدخول
            </button>
          </footer>
        </form>
      </div>
    </Modal>
  );
};

export default ReauthDialog;
